/**
 * Times on the restaurant's own clock (restaurants.timezone, sent by the server as
 * `timezone`), never the browser's. The API sends ISO 8601 with an offset; an old
 * answer without one is UTC (the database stores naive UTC), never local.
 */
export function parseApiTime(iso) {
  if (!iso) return null;
  const text = String(iso);
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(text);
  const t = Date.parse(hasZone || !/T\d/.test(text) ? text : `${text}Z`);
  return Number.isFinite(t) ? new Date(t) : null;
}

function safeZone(tz) {
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}

/** "Oct 29, 2026, 1:45 PM" in the restaurant's zone. */
export function formatDateTime(iso, tz, opts = {}) {
  const d = parseApiTime(iso);
  if (!d) return '-';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    timeZone: safeZone(tz), ...opts,
  });
}

/** "1:45 PM" in the restaurant's zone. */
export function formatTime(iso, tz, opts = {}) {
  const d = parseApiTime(iso);
  if (!d) return '-';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: safeZone(tz), ...opts });
}

/** A local calendar day "2026-10-29" (from the server's first_day / last_day) -> "Thu, Oct 29, 2026". */
export function formatDay(ymd, opts = {}) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return String(ymd);
  // noon UTC of that calendar day, shown in UTC: the same calendar day whatever the browser's zone
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC', ...opts,
  });
}
